// UTM / attribution capture.
//
// Reads UTMs from the current URL AND persists the first set we ever see in this
// browser (first-touch). This matters because visitors usually land on a UTM'd URL
// (e.g. /?utm_source=google) and then navigate to /contact before submitting. By
// then the URL has no query string. Without persistence, GHL receives blank UTMs.
// With it, every form submit carries attribution even pages-deep into the visit.

const FIRST_TOUCH_KEY = 'frayze_first_touch';

interface FirstTouch {
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  utm_term: string;
  utm_content: string;
  referrer: string;
  landing_page: string;
}

function readFirstTouch(): FirstTouch | null {
  try {
    return JSON.parse(localStorage.getItem(FIRST_TOUCH_KEY) || 'null');
  } catch {
    return null;
  }
}

export const getUTMData = () => {
  if (typeof window === 'undefined') return {};

  const p = new URLSearchParams(window.location.search);
  const fromUrl = {
    utm_source: p.get('utm_source') || '',
    utm_medium: p.get('utm_medium') || '',
    utm_campaign: p.get('utm_campaign') || '',
    utm_term: p.get('utm_term') || '',
    utm_content: p.get('utm_content') || '',
  };
  const hasUrlUtm = Object.values(fromUrl).some(Boolean);

  // First-touch: store the first landing we ever see (UTMs + referrer + landing page).
  let stored = readFirstTouch();
  if (!stored) {
    stored = {
      ...fromUrl,
      referrer: document.referrer || '',
      landing_page: window.location.href,
    };
    try {
      localStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify(stored));
    } catch {
      // localStorage unavailable (private mode). Fall through with in-memory values.
    }
  }

  // Prefer live URL UTMs when present (last-touch wins), else fall back to first-touch.
  const utm = hasUrlUtm
    ? fromUrl
    : {
        utm_source: stored.utm_source || '',
        utm_medium: stored.utm_medium || '',
        utm_campaign: stored.utm_campaign || '',
        utm_term: stored.utm_term || '',
        utm_content: stored.utm_content || '',
      };

  return {
    ...utm,
    referrer: document.referrer || stored.referrer || '',
    current_url: window.location.href,
  };
};
